/**
 * What each fee type actually charges on.
 *
 * A fee is one number, not two: a type either takes a flat amount, a
 * percentage, or lets you pick — never both at once. Collecting the field a
 * type ignores is how a 3.4% brokerage fee ends up carrying a stray fixed
 * amount that the pricing engine silently drops.
 *
 *   FIXED     flat amount only
 *   VARIABLE  percentage only
 *   FLOATING  percentage only — floating IS the variable rate
 *   STATIC    either
 *   ENTRY     either, and the only type that carries a level or per-exchange
 *   EXIT      either
 *   RECURRING either
 *
 * `level` says what an entry fee attaches to (asset class, all instruments in
 * it, or one instrument). No other type reads it, so no other type offers it.
 *
 * `perExchange` is an ORTHOGONAL modifier on top of the level, not a fourth
 * level: it narrows whatever the level already matches to positions held on the
 * same exchange, so entry is charged once per exchange instead of once overall.
 * Only entry fees read it, for the same reason as `level`.
 */
export const FEE_TYPE_RULES = {
  FIXED: {fixed: true, variable: false},
  VARIABLE: {fixed: false, variable: true},
  FLOATING: {fixed: false, variable: true},
  STATIC: {fixed: true, variable: true},
  ENTRY: {fixed: true, variable: true, level: true, perExchange: true},
  EXIT: {fixed: true, variable: true},
  RECURRING: {fixed: true, variable: true},
};

/**
 * Every fee type, in the order the editors list them.
 *
 * Derived from the rules above so the two editors cannot offer different sets.
 * They used to: the stacked card form hid FLOATING and STATIC unless it was
 * editing an exchange, so an instrument could not be given a static platform fee
 * from the UI even though the backend stores and charges one.
 */
export const FEE_TYPES = [
  'VARIABLE',
  'FIXED',
  'FLOATING',
  'STATIC',
  'ENTRY',
  'EXIT',
  'RECURRING',
];

/** 'VARIABLE' -> 'Variable', for display. */
export const feeTypeLabel = feeType =>
  feeType ? feeType.charAt(0) + feeType.slice(1).toLowerCase() : feeType;

/** Whether `feeType` charges on this field at all. `field` is fixed|variable. */
export const feeTypeUses = (feeType, field) =>
  Boolean(FEE_TYPE_RULES[feeType]?.[field]);

/** Only an entry fee carries a level. */
export const feeTypeHasLevel = feeType =>
  Boolean(FEE_TYPE_RULES[feeType]?.level);

/** Only an entry fee can be scoped per exchange. */
export const feeTypeHasPerExchange = feeType =>
  Boolean(FEE_TYPE_RULES[feeType]?.perExchange);

/**
 * Whether one amount input on a row is disabled.
 *
 * Two reasons it can be: the type does not charge on that field at all, or the
 * type accepts either and the other one is already filled in.
 */
export const isFeeAmountDisabled = (fee, field) => {
  const feeType = fee?.feeType;
  if (!feeType) return true;
  if (!feeTypeUses(feeType, field)) return true;
  const other = field === 'fixed' ? 'variableFeeAmount' : 'fixedFeeAmount';
  return Boolean(fee?.[other]);
};

/**
 * Strips whatever the chosen type does not use.
 *
 * Called when the type changes, so switching Variable → Fixed does not leave
 * the old percentage behind on a row that no longer charges one.
 */
export const pruneFeeForType = fee => {
  const next = {...fee};
  if (!feeTypeUses(next.feeType, 'fixed')) delete next.fixedFeeAmount;
  if (!feeTypeUses(next.feeType, 'variable')) delete next.variableFeeAmount;
  if (!feeTypeHasLevel(next.feeType)) delete next.level;
  if (!feeTypeHasPerExchange(next.feeType)) delete next.perExchange;
  return next;
};
